/**
 * Fellido · Warenkorb
 * Nachbau des Live-Warenkorbs (cro-cart) auf Basis der Shopify Cart API.
 * Geschenkzeilen werden an der Eigenschaft aus den Section-Settings erkannt
 * und getrennt mit Streichwert und 0,00 € dargestellt.
 */
class FCart extends HTMLElement {
  connectedCallback() {
    this.giftProperty = this.dataset.giftProperty || '_geschenk';
    this.freeLabel = this.dataset.freeLabel || '0,00 €';
    this.busy = false;
    this.produktCache = new Map();

    this.lines = this.querySelector('[data-f-cart-lines]');
    this.giftWrap = this.querySelector('[data-f-cart-gifts]');
    this.giftLines = this.querySelector('[data-f-cart-gift-lines]');
    this.empty = this.querySelector('[data-f-cart-empty]');
    this.foot = this.querySelector('[data-f-cart-foot]');
    this.countEl = this.querySelector('[data-f-cart-count]');
    this.totalEl = this.querySelector('[data-f-cart-total]');
    this.worthEl = this.querySelector('[data-f-cart-worth]');
    this.worthRow = this.querySelector('[data-f-cart-worth-row]');
    this.saveBox = this.querySelector('[data-f-cart-save]');
    this.saveEl = this.querySelector('[data-f-cart-savings]');

    this.upsTrack = this.querySelector('[data-f-ups-track]');
    this.upsNav = this.querySelector('[data-f-ups-nav]');

    this.addEventListener('click', this.onClick);
    document.addEventListener('keydown', this.onKey);
    document.addEventListener('click', this.onDocClick, true);

    // Nach jedem Hinzufuegen aktualisieren und oeffnen.
    // Shopify Standard Events feuert 'cart:update'; zusaetzlich faengt ein
    // submit-capture den Fall ab, dass ein Formular ohne Event durchlaeuft.
    document.addEventListener('cart:update', () => this.refresh().then(() => this.open()));
    document.addEventListener('submit', (event) => {           // submit-capture
      const form = event.target;
      if (!form?.getAttribute || !(form.getAttribute('action') || '').includes('/cart/add')) return;
      setTimeout(() => this.refresh().then(() => this.open()), 700);
    }, true);

    // Im Theme-Editor: beim Anklicken der Section den Drawer oeffnen,
    // sonst laesst er sich dort nicht ansehen oder bearbeiten.
    const eigeneSection = this.closest('.shopify-section');
    document.addEventListener('shopify:section:select', (event) => {
      if (eigeneSection && event.target === eigeneSection) this.refresh().then(() => this.open());
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      if (eigeneSection && event.target === eigeneSection) this.close();
    });

    this.refresh();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('click', this.onDocClick, true);
  }

  /** Jeder Weg zum Warenkorb landet im Drawer statt auf /cart. */
  onDocClick = (event) => {
    const trigger = event.target.closest(
      'a[href$="/cart"], a[href*="/cart?"], [aria-label="Warenkorb"], [data-f-cart-open]'
    );
    if (!trigger || this.contains(trigger)) return;
    event.preventDefault();
    event.stopPropagation();
    this.refresh().then(() => this.open());
  };

  onKey = (event) => {
    if (event.key === 'Escape' && this.dataset.state === 'open') this.close();
  };

  onClick = async (event) => {
    if (event.target.closest('[data-f-cart-close]')) return this.close();

    // Pfeile der Empfehlungsreihe
    const pfeil = event.target.closest('[data-f-ups-prev], [data-f-ups-next]');
    if (pfeil && this.upsTrack) {
      const karte = this.upsTrack.querySelector('[data-f-upsell]:not([hidden])');
      // Eine Karte plus Abstand weiterschieben, nicht eine feste Pixelzahl.
      const schritt = karte ? karte.getBoundingClientRect().width + 10 : this.upsTrack.clientWidth * 0.8;
      this.upsTrack.scrollBy({ left: pfeil.hasAttribute('data-f-ups-next') ? schritt : -schritt, behavior: 'smooth' });
      return;
    }

    // Empfehlung hinzufuegen
    const upBtn = event.target.closest('[data-f-upsell-add]');
    if (upBtn && !this.busy) {
      this.busy = true;
      const label = upBtn.textContent;
      upBtn.disabled = true;
      upBtn.textContent = '…';
      const wahl = upBtn.closest('[data-f-upsell]')?.querySelector('[data-f-upsell-variant]');
      const variantId = Number(wahl ? wahl.value : upBtn.dataset.variant);
      try {
        await fetch(`${this.root()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        await this.refresh();
      } catch (error) {
        console.warn('[Fellido] Empfehlung konnte nicht hinzugefügt werden:', error);
      } finally {
        upBtn.disabled = false;
        upBtn.textContent = label;
        this.busy = false;
      }
      return;
    }

    const line = event.target.closest('[data-key]');
    if (!line || this.busy) return;
    const key = line.dataset.key;
    const aktuell = Number(line.querySelector('[data-qty]')?.textContent) || 1;

    let menge = null;
    if (event.target.closest('[data-inc]')) menge = aktuell + 1;
    else if (event.target.closest('[data-dec]')) menge = Math.max(0, aktuell - 1);
    else if (event.target.closest('[data-rm]')) menge = 0;
    if (menge === null) return;

    this.busy = true;
    try {
      await fetch(`${this.root()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: menge }),
      });
      await this.refresh();
    } finally {
      this.busy = false;
    }
  };

  root() {
    return window.Shopify?.routes?.root || '/';
  }

  open() {
    this.dataset.state = 'open';
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.querySelector('[data-f-cart-close]')?.focus();
  }

  close() {
    this.dataset.state = 'closed';
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  /**
   * Ein Geschenk ist, was nichts kostet — nicht, was so markiert ist.
   *
   * Die Markierung `_geschenk` setzt der Geschenkwaehler. Shopifys
   * Bxgy-Rabatt kennt sie aber nicht: Er sucht sich selbst eine passende
   * Zeile und trifft dabei auch mal eine andere. Verliesse sich der Drawer
   * auf die Markierung, stuende "GESCHENK" ueber einer Zeile, die der Kunde
   * voll bezahlt. Der Endpreis luegt nicht, die Markierung schon.
   */
  istGeschenk(item) {
    return item.final_line_price === 0;
  }

  /**
   * Warenwert aus den echten Streichpreisen bilden.
   * cart.js liefert keinen compare_at_price — deshalb je Produkt einmal
   * /products/<handle>.js nachladen und das Ergebnis merken.
   */
  async warenwertAusStreichpreisen(cart) {
    const items = cart.items || [];
    if (items.length === 0) return;

    const handles = [...new Set(items.map((i) => i.handle).filter(Boolean))];
    await Promise.all(
      handles.map(async (handle) => {
        if (this.produktCache.has(handle)) return;
        try {
          const daten = await (await fetch(`${this.root()}products/${handle}.js`)).json();
          const karte = new Map();
          (daten.variants || []).forEach((v) => karte.set(v.id, v.compare_at_price || 0));
          this.produktCache.set(handle, karte);
        } catch {
          this.produktCache.set(handle, new Map());
        }
      })
    );

    const warenwert = items.reduce((summe, i) => {
      const karte = this.produktCache.get(i.handle);
      const streich = karte?.get(i.variant_id) || 0;
      const basis = streich > i.original_price ? streich : i.original_price;
      return summe + basis * i.quantity;
    }, 0);

    if (warenwert > cart.total_price) this.zeigeErsparnis(warenwert, cart.total_price);
    this.zeileStreichpreise(items);
  }

  zeileStreichpreise(items) {
    items.forEach((i) => {
      if (this.istGeschenk(i)) return;
      const karte = this.produktCache.get(i.handle);
      const streich = karte?.get(i.variant_id) || 0;
      if (streich * i.quantity <= i.final_line_price) return;
      const sel = (window.CSS && CSS.escape) ? CSS.escape(i.key) : i.key;
      const el = this.lines.querySelector(`[data-key="${sel}"] [data-line-price]`);
      if (el) el.innerHTML = `<s>${this.geld(streich * i.quantity)}</s>${this.geld(i.final_line_price)}`;
    });
  }

  zeigeErsparnis(warenwert, zahlbetrag) {
    const gespart = warenwert - zahlbetrag;
    if (this.saveBox) {
      this.saveBox.hidden = gespart <= 0;
      if (this.saveEl) this.saveEl.textContent = `− ${this.geld(gespart)}`;
    }
    if (this.worthEl) this.worthEl.textContent = this.geld(warenwert);
    if (this.worthRow) this.worthRow.hidden = gespart <= 0;
  }

  /**
   * Pfeile nur zeigen, wenn die Reihe wirklich ueberlaeuft, und am jeweiligen
   * Ende ausgrauen. Sonst klickt man ins Leere.
   */
  pfeileBewerten() {
    if (!this.upsNav || !this.upsTrack) return;
    const laeuftUeber = this.upsTrack.scrollWidth > this.upsTrack.clientWidth + 4;
    this.upsNav.hidden = !laeuftUeber;
    if (!laeuftUeber) return;

    const prev = this.upsNav.querySelector('[data-f-ups-prev]');
    const next = this.upsNav.querySelector('[data-f-ups-next]');
    const pruefe = () => {
      const x = this.upsTrack.scrollLeft;
      const max = this.upsTrack.scrollWidth - this.upsTrack.clientWidth;
      if (prev) prev.disabled = x <= 2;
      if (next) next.disabled = x >= max - 2;
    };
    pruefe();
    if (!this.upsHorcht) {
      this.upsTrack.addEventListener('scroll', pruefe, { passive: true });
      this.upsHorcht = true;
    }
  }

  geld(cents) {
    return `${(cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  }

  bild(url, groesse) {
    return url ? url.replace(/(\.[a-z]+)(\?|$)/i, `_${groesse}x${groesse}$1$2`) : '';
  }

  async refresh() {
    const cart = await (await fetch(`${this.root()}cart.js`, { headers: { Accept: 'application/json' } })).json();
    this.render(cart);
    return cart;
  }

  render(cart) {
    const items = cart.items || [];
    const bezahlt = items.filter((i) => !this.istGeschenk(i));
    const geschenke = items.filter((i) => this.istGeschenk(i));

    if (this.countEl) {
      this.countEl.textContent = String(bezahlt.reduce((a, i) => a + i.quantity, 0));
      this.countEl.hidden = bezahlt.length === 0;
    }

    if (bezahlt.length === 0) {
      this.empty.hidden = false;
      this.lines.innerHTML = '';
      this.giftWrap.hidden = true;
      this.foot.hidden = true;
      return;
    }
    this.empty.hidden = true;
    this.foot.hidden = false;

    this.lines.innerHTML = bezahlt
      .map(
        (i) => `
      <div class="f-cart__line" data-key="${i.key}">
        ${i.image ? `<img class="f-cart__line-img" src="${this.bild(i.image, 128)}" alt="" loading="lazy">` : '<span class="f-cart__line-img"></span>'}
        <div class="f-cart__line-mid">
          <span class="f-cart__line-t">${i.product_title}</span>
          ${i.variant_title ? `<span class="f-cart__line-v">${i.variant_title}</span>` : ''}
          <div class="f-cart__qty">
            <button type="button" data-dec aria-label="Menge verringern">−</button>
            <span data-qty>${i.quantity}</span>
            <button type="button" data-inc aria-label="Menge erhöhen">+</button>
          </div>
        </div>
        <div class="f-cart__line-r">
          <span class="f-cart__line-p" data-line-price>${this.geld(i.final_line_price)}</span>
          <button class="f-cart__rm" type="button" data-rm>entfernen</button>
        </div>
      </div>`
      )
      .join('');

    if (geschenke.length > 0) {
      this.giftLines.innerHTML = geschenke
        .map(
          (i) => `
        <div class="f-cart__gift" data-key="${i.key}">
          ${i.image ? `<img class="f-cart__gift-img" src="${this.bild(i.image, 112)}" alt="" loading="lazy">` : '<span class="f-cart__gift-img"></span>'}
          <div class="f-cart__gift-mid">
            <span class="f-cart__gift-t">${i.product_title}</span>
            ${i.variant_title ? `<span class="f-cart__gift-v">${i.variant_title}</span>` : ''}
            <span class="f-cart__gift-badge">Geschenk</span>
          </div>
          <div class="f-cart__gift-p">
            ${i.final_line_price < i.original_line_price ? `<s>${this.geld(i.original_line_price)}</s>` : ''}
            <b>${i.final_line_price === 0 ? this.freeLabel : this.geld(i.final_line_price)}</b>
          </div>
        </div>`
        )
        .join('');
      this.giftWrap.hidden = false;
    } else {
      this.giftLines.innerHTML = '';
      this.giftWrap.hidden = true;
    }

    // Zunaechst mit dem, was cart.js hergibt: Rabatte.
    // Die Streichpreise kennt cart.js nicht — die werden gleich nachgeladen.
    this.zeigeErsparnis(cart.total_price + (cart.total_discount || 0), cart.total_price);
    this.warenwertAusStreichpreisen(cart);
    if (this.totalEl) this.totalEl.textContent = this.geld(cart.total_price);

    // Empfehlungen ausblenden, die schon im Warenkorb liegen
    const drin = new Set(bezahlt.map((i) => String(i.product_id)));
    const upWrap = this.querySelector('[data-f-cart-upsells]');
    if (upWrap) {
      let sichtbar = 0;
      upWrap.querySelectorAll('[data-f-upsell]').forEach((el) => {
        const zeigen = !drin.has(el.dataset.productId);
        el.hidden = !zeigen;
        if (zeigen) sichtbar += 1;
      });
      upWrap.hidden = sichtbar === 0 || bezahlt.length === 0;
    }
    this.pfeileBewerten();
  }
}

if (!customElements.get('f-cart')) {
  if (!customElements.get('f-cart')) customElements.define('f-cart', FCart);
}
